// src/Components/UserActivities/ActivityGraphs.jsx

import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    useTheme,
    Card,
    CardContent,
    Stack,
    Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { BarChart, LineChart } from '@mui/x-charts';

// helper for area gradients
function AreaGradient({ id, color }) {
    return (
        <defs>
            <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
                <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
        </defs>
    );
}

// all your activity types
const ACTIVITY_TYPES = [
    'Upload',
    'Download',
    'GenerateDocument',
    'ImportData',
    'ExportData',
    'CreateEntities',
    'DeleteData',
    'EditData',
    'ViewData',

];

// how to pair them in the grid below
const GROUPS = [
    ['Upload', 'Download'],
    ['ImportData', 'ExportData'],
    ['CreateEntities', 'DeleteData'],
    ['EditData', 'ViewData'],
];

export default function ActivityGraphs({ summaries, direction }) {
    const { t } = useTranslation();
    const theme = useTheme();

    if (!Array.isArray(summaries) || summaries.length === 0) {
        return (
            <Typography variant="body2" sx={{ mt: 2 }}>
                {t('NoDataToDisplay')}
            </Typography>
        );
    }

    // 1) prepare x axis and total line
    const dates = summaries.map((d) => dayjs(d.date).toDate());
    const totalCounts = summaries.map((d) => d.totalExcludingOther);
    const periodTotal = totalCounts.reduce((sum, n) => sum + n, 0);

    // compute percent change for the line chart
    const first = totalCounts[0] || 0;
    const last = totalCounts[totalCounts.length - 1] || 0;
    const pctChange =
        first > 0 ? Math.round(((last - first) / first) * 100) : 0;
    const pctLabel = `${last >= first ? '+' : ''}${pctChange}%`;

    // 2) prepare overview bar data
    const overviewCategories = ACTIVITY_TYPES.map((k) => t(k));
    const overviewValues = ACTIVITY_TYPES.map((k) =>
        summaries.reduce((sum, d) => sum + (d.counts[k] || 0), 0)
    );

    // 3) shared xAxis for line charts
    const xAxisConfig = {
        data: dates,
        scaleType: 'time',
        nice: true,
        label: t('Date'),
        valueFormatter: (val, ctx) =>
            ctx.location === 'tick'
                ? dayjs(val).format('D/M')
                : dayjs(val).format('D/M/YYYY H:mm:ss'),
    };

    const barColors2 = [
        '#f9aa2a', // Upload – deep sky blue
        '#304fff', // DeleteData – lime punch
    ];


    // 4) colors for the bar chart: one unique HSL per category
    const barColors = ACTIVITY_TYPES.map(
        (_, idx) => barColors2[idx % barColors2.length]
    );


    // inside ActivityGraphs.jsx, above your return:
    // inside ActivityGraphs.jsx, replace your existing GroupedChart with this:

    function GroupedChart({ keys, title, summaries, xAxisConfig, barColors, t, direction }) {
        // 1️⃣ initialize visibility to “everything visible”
        const [visibleSet, setVisibleSet] = React.useState(() => new Set(keys))

        // 2️⃣ handler that toggles a key in the set
        const handleToggle = (key) =>
            setVisibleSet((prev) => {
                const next = new Set(prev)
                if (next.has(key)) next.delete(key)
                else next.add(key)
                return next
            })

        // 3️⃣ build your series, using `visibleSet.has(k)` to decide
        const allSeries = keys.map((k) => ({
            id: k,
            label: t(k),
            data: summaries.map((d) => d.counts[k] ?? 0),
            showMark: false,
            area: true,
            color: barColors[ACTIVITY_TYPES.indexOf(k) % barColors.length],
            visible: visibleSet.has(k),
        }))

        const series = allSeries.filter(s => visibleSet.has(s.id))

        return (
            <Card variant="outlined">
                <CardContent>
                    <Typography component="h3" variant="subtitle2" gutterBottom>
                        {title}
                    </Typography>

                    {/* 4️⃣ Custom legend: Chips that toggle visibility */}
                    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        {keys.map((k) => (
                            <Chip
                                key={k}
                                label={t(k)}
                                size="small"
                                clickable
                                variant={visibleSet.has(k) ? 'filled' : 'outlined'}
                                onClick={() => handleToggle(k)}
                                sx={{
                                    backgroundColor: visibleSet.has(k)
                                        ? barColors[ACTIVITY_TYPES.indexOf(k) % barColors.length]
                                        : undefined,
                                    color: visibleSet.has(k) ? '#fff' : undefined,
                                }}
                            />
                        ))}
                    </Stack>

                    {/* 5️⃣ Chart with only the “visible” series drawn */}
                    <LineChart
                        xAxis={[xAxisConfig]}
                        height={180}
                        series={series}
                        slotProps={{
                            legend: { hidden: true },  // hide built‐in legend
                            grid: { horizontal: true },
                        }}
                        sx={keys.reduce((acc, k) => {
                            acc[`.MuiAreaElement-series-${k}`] = {
                                fill: `url(#grad-${k})`,
                            }
                            return acc
                        }, {})}
                        dir={direction}
                    >
                        {keys.map((k) => (
                            <AreaGradient
                                key={k}
                                id={`grad-${k}`}
                                color={barColors[ACTIVITY_TYPES.indexOf(k) % barColors.length]}
                            />
                        ))}
                    </LineChart>
                </CardContent>
            </Card>
        )
    }



    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 4,
                mt: 2,
                px: 1,
            }}
            dir={direction}
        >
            {/* — Overview BarChart — */}
            <Card variant="outlined" sx={{ height: 400 }}>
                <CardContent>
                    <Typography component="h2" variant="subtitle2" gutterBottom>
                        {t('ActivitiesOverview')}
                    </Typography>

                    {/* Top row: total */}
                    <Stack sx={{ justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h4">
                            {overviewValues.reduce((a, b) => a + b, 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('OverViewForAllSystemActivities')}
                        </Typography>
                    </Stack>

                    <BarChart
                        borderRadius={8}
                        colors={barColors2} // <- base palette
                        hideLegend
                        xAxis={[{
                            scaleType: 'band',
                            categoryGapRatio: 0.5,
                            data: overviewCategories,
                            height: 40, // space for labels
                            label: t('ActivityType'), // X-axis title
                            labelProps: {
                                style: {
                                    fontSize: '12px',
                                    fontWeight: 600,
                                },
                            },
                            tickLabelStyle: {
                                fontSize: 11,
                                textAnchor: 'end',
                            },
                            colorMap: {
                                type: 'ordinal',
                                colors: barColors, // <- keep this as is
                            },
                        }]}
                        yAxis={[{
                            width: 50,
                            labelProps: {
                                style: {
                                    fontSize: '12px',
                                    fontWeight: 600,
                                },
                            },
                            tickLabelStyle: {
                                fontSize: 11,
                            },
                        }]}
                        series={[{
                            id: 'overview',
                            label: t('Total'),
                            data: overviewValues,
                            stack: 'A',
                        }]}
                        height={260}
                        margin={{ left: 40, right: 10, top: 20, bottom: 60 }}
                        grid={{ horizontal: true }}
                        slotProps={{
                            legend: { hidden: true },
                            barLabel: {
                                visible: true,
                                dy: -4,
                                style: {
                                    fontSize: '12px',
                                    fontWeight: 600,
                                },
                                formatter: (v) => v,
                            },
                        }}
                    />



                </CardContent>
            </Card>

            {/* — Total Over Time with Percentage — */}
            <Card variant="outlined" sx={{ height: 400 }}>
                <CardContent>
                    <Typography component="h2" variant="subtitle2" gutterBottom>
                        {t('total activities in the system')}
                    </Typography>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        <Typography variant="h4">
                            {periodTotal.toLocaleString()}
                        </Typography>

                    </Stack>
                    <LineChart

                        xAxis={[xAxisConfig]}
                        yAxis={[
                            {
                                // Y‐axis title “Hours”
                                label: t("Total"),
                                position: 'top',
                                labelProps: { style: { fontSize: 12, fontWeight: 900 } },
                                dy: -50,
                                textAnchor: "middle"
                            }
                        ]}
                        series={[
                            {
                                data: totalCounts,
                                label: t('Total'),
                                showMark: false,
                                area: true,
                                stroke: theme.palette.primary.main,
                            },
                        ]}
                        height={280}
                        slotProps={{
                            legend: { position: { vertical: 'top', horizontal: 'right' } },
                        }}
                        sx={{
                            '& .MuiAreaElement-series-Total': {
                            },
                        }}
                    >
                        <AreaGradient id="grad-daily" color="#FFD700" />
                    </LineChart>
                </CardContent>
            </Card>

            {/* — Grouped Activity Charts — */}
            <Box gridColumn="1 / -1">
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3,1fr)',
                        gap: 3,
                        mt: 2,
                    }}
                >
                    {GROUPS.map((keys) => {
                        const title =
                            keys.length > 1
                                ? keys.map((k) => t(k)).join(' + ')
                                : t(keys[0]);

                        return (
                            <GroupedChart
                                key={keys.join('-')}
                                keys={keys}
                                title={title}
                                summaries={summaries}
                                xAxisConfig={xAxisConfig}
                                barColors={barColors2}
                                t={t}
                                direction={direction}
                            />
                        );
                    })}
                </Box>
            </Box>

        </Box >
    );
}

ActivityGraphs.propTypes = {
    summaries: PropTypes.arrayOf(
        PropTypes.shape({
            date: PropTypes.string.isRequired,
            counts: PropTypes.object.isRequired,
            totalExcludingOther: PropTypes.number.isRequired,
        })
    ).isRequired,
    direction: PropTypes.oneOf(['ltr', 'rtl']).isRequired,
};
